import React, { useContext, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SITE_URL, DEFAULT_SHARE_IMAGE, publicImage } from "../lib/publicSite";
import { SearchPrivacyContext } from "./SearchPrivacy";

/**
 * <SEO /> — single source of truth for per-page meta tags and JSON-LD.
 *
 * Why this exists:
 *   We're a single-page React app, so the static index.html has only one set of
 *   meta tags. Crawlers that DO run JS (modern Googlebot) will pick up these
 *   Helmet-managed tags AND the structured-data JSON-LD blocks below. Crawlers
 *   that don't (Facebook/LinkedIn/most preview bots) should be pointed at the
 *   SSR HTML served by the FastAPI OG-proxy routes — see /api/og/* endpoints.
 *
 * Usage:
 *   <SEO
 *     title="About Reddog Community Foundation"
 *     description="A community-funded, board-governed nonprofit…"
 *     path="/about"
 *     image="/og/about.png"             // absolute or path; will be made absolute
 *     jsonLd={[{...}]}                  // optional list of JSON-LD objects
 *   />
 */
const SITE_NAME = "Reddog Community Foundation";
const DEFAULT_DESC =
    "Support school, family and community needs in Frenchtown, Montana. Donate, shop and follow Reddog Community Foundation's public ledger.";
const DEFAULT_IMAGE = DEFAULT_SHARE_IMAGE;

/**
 * Default organization-level JSON-LD that appears on every page.
 * Establishes the nonprofit's identity in Google's Knowledge Graph.
 */
const ORGANIZATION_LD = {
    "@context": "https://schema.org",
    "@type": "NGO",
    name: SITE_NAME,
    legalName: "Reddog Community Foundation Inc.",
    url: SITE_URL,
    image: `${SITE_URL}/og-default.png`,
    description:
        "An independent nonprofit corporation supporting school, family and community needs in Frenchtown, Montana through transparent fundraising and responsible oversight.",
    address: {
        "@type": "PostalAddress",
        addressLocality: "Frenchtown",
        addressRegion: "MT",
        addressCountry: "US",
    },
    sameAs: [],
};

export const SEO = ({
    title,
    description = DEFAULT_DESC,
    path = "",
    image = DEFAULT_IMAGE,
    type = "website",
    noIndex = false,
    jsonLd = null,
}) => {
    const location = useLocation();
    const setPagePolicy = useContext(SearchPrivacyContext);
    useEffect(() => { setPagePolicy?.({ key: location.key, noIndex }); }, [setPagePolicy, location.key, noIndex]);
    const fullTitle = title ? `${title} · ${SITE_NAME}` : SITE_NAME;
    const url = `${SITE_URL}${path || "/"}`;
    const fullImage = publicImage(image);
    const ldBlocks = [ORGANIZATION_LD, ...(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [])];
    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={url} />

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={fullImage} />
            <meta property="og:image:alt" content={title || SITE_NAME} />
            {(!image || image === DEFAULT_IMAGE) && <meta property="og:image:width" content="1200" />}
            {(!image || image === DEFAULT_IMAGE) && <meta property="og:image:height" content="630" />}

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={fullImage} />

            {/* Structured data — Organization always present, plus any per-page LD passed in */}
            {ldBlocks.map((block, i) => (
                <script key={`ld-${i}`} type="application/ld+json">
                    {JSON.stringify(block).replace(/</g, "\\u003c")}
                </script>
            ))}
        </Helmet>
    );
};

export default SEO;
